import React, { useState, useRef } from 'react';
import { X, Upload, FileVideo, Image as ImageIcon, Loader2 } from 'lucide-react';

interface SimpleUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: { type: 'video' | 'image', file: File, previewUrl: string, thumbnailUrl: string, title?: string, description?: string }) => void;
    zIndex?: number;
}

const SimpleUploadModal: React.FC<SimpleUploadModalProps> = ({ isOpen, onClose, onSave, zIndex = 50 }) => {
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [thumbnailUrl, setThumbnailUrl] = useState<string>('');
    const [fileType, setFileType] = useState<'video' | 'image'>('image');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!isOpen) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const generateVideoThumbnail = (file: File): Promise<string> => {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.src = URL.createObjectURL(file);
            video.muted = true;
            video.playsInline = true;
            video.onloadeddata = () => {
                video.currentTime = Math.min(1, video.duration / 2 || 0.1);
            };
            video.onseeked = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth || 640;
                    canvas.height = video.videoHeight || 360;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL('image/jpeg'));
                } catch {
                    resolve('');
                }
            };
            video.onerror = () => resolve('');
        });
    };

    const processFile = async (file: File) => {
        const type = file.type.startsWith('video/') ? 'video' : 'image';
        setFileType(type);
        setFile(file);

        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        setThumbnailUrl('');

        if (type === 'video') {
            const thumb = await generateVideoThumbnail(file);
            setThumbnailUrl(thumb);
        } else {
            setThumbnailUrl(url);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (file && previewUrl) {
            onSave({
                type: fileType,
                file,
                previewUrl,
                thumbnailUrl: thumbnailUrl || previewUrl,
                title: title || undefined,
                description: description || undefined
            });
            // Reset and close
            setFile(null);
            setPreviewUrl('');
            setThumbnailUrl('');
            setTitle('');
            setDescription('');
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" style={{ zIndex }}>
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h3 className="text-lg font-bold text-gray-900">上传示例作品</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Upload Area */}
                        {!file ? (
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                                onDragLeave={() => setIsDragOver(false)}
                                onDrop={handleDrop}
                                className={`
                                    border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center gap-4 cursor-pointer transition-all
                                    ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                                    h-48
                                `}
                            >
                                <div className="p-4 bg-white rounded-full shadow-sm">
                                    <Upload className={`w-8 h-8 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                                </div>
                                <div className="text-center">
                                    <p className="font-medium text-gray-900">点击或拖拽上传</p>
                                    <p className="text-sm text-gray-400 mt-1">支持 JPG, PNG, MP4</p>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    className="hidden"
                                    accept="image/*,video/*"
                                    onChange={handleFileChange}
                                />
                            </div>
                        ) : (
                            <div className="relative rounded-xl overflow-hidden border border-gray-100 bg-black group h-48 flex items-center justify-center">
                                {fileType === 'video' ? (
                                    <video src={previewUrl} className="w-full h-full object-contain" controls />
                                ) : (
                                    <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
                                )}
                                <button
                                    type="button"
                                    onClick={() => { setFile(null); setPreviewUrl(''); }}
                                    className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* Fields */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                    标题 <span className="text-gray-300 font-normal">(可选)</span>
                                </label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    placeholder="作品标题"
                                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-black/5 transition-all outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                    描述 <span className="text-gray-300 font-normal">(可选)</span>
                                </label>
                                <textarea
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    placeholder="简单的作品描述..."
                                    rows={3}
                                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-black/5 transition-all outline-none resize-none"
                                />
                            </div>
                        </div>

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={!file}
                                className={`
                                    w-full py-3 rounded-full font-bold text-white transition-all
                                    ${file ? 'bg-black hover:bg-gray-800 shadow-lg hover:shadow-xl' : 'bg-gray-200 cursor-not-allowed'}
                                `}
                            >
                                确认上传
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SimpleUploadModal;
